import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const CostCalculator = () => {
  const [inputs, setInputs] = useState({
    currentAISpend: '',
    employees: '',
    aiModels: '',
    dataVolume: ''
  });
  
  const [results, setResults] = useState(null);
  const [timeframe, setTimeframe] = useState(5);

  const calculateSavings = () => {
    const currentSpend = parseFloat(inputs?.currentAISpend) || 0;
    const employees = parseInt(inputs?.employees) || 0;
    const models = parseInt(inputs?.aiModels) || 0;
    const dataVolume = parseFloat(inputs?.dataVolume) || 0;

    // Proprietary cost calculations (with annual increases)
    const proprietaryAnnualIncrease = 0.15; // 15% annual increase
    const proprietaryCosts = [];
    let proprietaryTotal = 0;

    for (let year = 1; year <= timeframe; year++) {
      const yearCost = currentSpend * Math.pow(1 + proprietaryAnnualIncrease, year - 1);
      proprietaryCosts?.push(yearCost);
      proprietaryTotal += yearCost;
    }

    // Open-source cost calculations
    const initialImplementation = currentSpend * 0.3; // 30% of current spend for implementation
    const annualMaintenance = currentSpend * 0.08; // 8% annual maintenance
    const openSourceTotal = initialImplementation + (annualMaintenance * timeframe);

    const totalSavings = proprietaryTotal - openSourceTotal;
    const savingsPercentage = ((totalSavings / proprietaryTotal) * 100)?.toFixed(1);

    setResults({
      proprietaryTotal,
      openSourceTotal,
      totalSavings,
      savingsPercentage,
      initialImplementation,
      annualMaintenance,
      proprietaryCosts
    });
  };

  const handleInputChange = (field, value) => {
    setInputs(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(amount);
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-brand-primary mb-6">
            Vendor Lock-in Risk Calculator
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            See the true cost of proprietary AI dependency over time. Most organizations are shocked 
            by the long-term financial impact of vendor lock-in.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Input Form */}
          <div className="bg-gray-50 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-brand-primary mb-8">
              Your Current AI Investment
            </h3>
            
            <div className="space-y-6">
              <Input
                label="Annual AI Software Spend"
                type="number"
                placeholder="250000"
                description="Include all AI/ML software licenses and subscriptions"
                value={inputs?.currentAISpend}
                onChange={(e) => handleInputChange('currentAISpend', e?.target?.value)}
              />

              <Input
                label="Number of Employees Using AI"
                type="number"
                placeholder="150"
                description="Employees who regularly use AI tools"
                value={inputs?.employees}
                onChange={(e) => handleInputChange('employees', e?.target?.value)}
              />

              <Input
                label="AI Models/Services in Use"
                type="number"
                placeholder="8"
                description="Different AI services or model subscriptions"
                value={inputs?.aiModels}
                onChange={(e) => handleInputChange('aiModels', e?.target?.value)}
              />

              <Input
                label="Monthly Data Processing (GB)"
                type="number"
                placeholder="500"
                description="Data processed through AI services monthly"
                value={inputs?.dataVolume}
                onChange={(e) => handleInputChange('dataVolume', e?.target?.value)}
              />

              {/* Timeframe Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Projection Timeframe
                </label>
                <div className="flex space-x-4">
                  {[3, 5, 10]?.map((years) => (
                    <button
                      key={years}
                      onClick={() => setTimeframe(years)}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                        timeframe === years
                          ? 'bg-brand-primary text-white' :'bg-white text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {years} Years
                    </button>
                  ))}
                </div>
              </div>

              <Button
                variant="default"
                fullWidth
                onClick={calculateSavings}
                className="bg-brand-primary hover:bg-brand-primary/90"
                iconName="Calculator"
                iconPosition="left"
              >
                Calculate My Savings
              </Button>
            </div>
          </div>

          {/* Results */}
          <div className="space-y-6">
            {results ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                    <div className="flex items-center mb-2">
                      <Icon name="TrendingUp" size={20} className="text-red-500 mr-2" />
                      <span className="text-sm font-medium text-red-700">Proprietary Cost</span>
                    </div>
                    <div className="text-2xl font-bold text-red-600">
                      {formatCurrency(results?.proprietaryTotal)}
                    </div>
                    <div className="text-sm text-red-600">Over {timeframe} years</div>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                    <div className="flex items-center mb-2">
                      <Icon name="TrendingDown" size={20} className="text-green-500 mr-2" />
                      <span className="text-sm font-medium text-green-700">Open-Source Cost</span>
                    </div>
                    <div className="text-2xl font-bold text-green-600">
                      {formatCurrency(results?.openSourceTotal)}
                    </div>
                    <div className="text-sm text-green-600">Over {timeframe} years</div>
                  </div>
                </div>

                {/* Savings Highlight */}
                <div className="bg-gradient-to-r from-brand-primary to-brand-secondary rounded-2xl p-8 text-white">
                  <div className="text-center">
                    <div className="text-sm font-medium text-blue-200 mb-2">Total Savings</div>
                    <div className="text-4xl font-bold mb-2">
                      {formatCurrency(results?.totalSavings)}
                    </div>
                    <div className="text-lg text-blue-200">
                      {results?.savingsPercentage}% cost reduction over {timeframe} years
                    </div>
                  </div>
                </div>

                {/* Breakdown */}
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h4 className="text-lg font-bold text-brand-primary mb-4">Cost Breakdown</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Initial Implementation</span>
                      <span className="font-semibold">{formatCurrency(results?.initialImplementation)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Annual Maintenance</span>
                      <span className="font-semibold">{formatCurrency(results?.annualMaintenance)}</span>
                    </div>
                    <div className="border-t pt-3 flex justify-between">
                      <span className="font-semibold">Open-Source Total</span>
                      <span className="font-bold text-green-600">{formatCurrency(results?.openSourceTotal)}</span>
                    </div>
                  </div>
                </div>

                {/* Action */}
                <div className="bg-brand-accent/10 border border-brand-accent/20 rounded-xl p-6">
                  <div className="flex items-start">
                    <Icon name="Lightbulb" size={24} className="text-brand-accent mr-3 mt-1" />
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">Ready to Capture These Savings?</h4>
                      <p className="text-gray-600 mb-4">
                        Our AI Core Blueprint shows you exactly how to migrate from proprietary to open-source AI 
                        while maintaining performance and security.
                      </p>
                      <Button
                        variant="default"
                        className="bg-brand-accent hover:bg-brand-accent/90 text-gray-900"
                        iconName="Download"
                        iconPosition="left"
                      >
                        Get Migration Blueprint
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-gray-50 rounded-2xl p-12 text-center">
                <Icon name="Calculator" size={48} className="text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-600 mb-2">
                  Calculate Your Potential Savings
                </h3>
                <p className="text-gray-500">
                  Fill in your current AI investment details to see how much you could save 
                  with open-source alternatives.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CostCalculator;